import React, { useState, useMemo } from 'react';
import { FaCalculator, FaUserClock } from 'react-icons/fa';

// Helper to format numbers as Indian Rupee (e.g., ₹1,20,00,000)
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
};

function RetirementCalculator() {
  // --- Input States ---
  const [presentAge, setPresentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [monthlyExpenses, setMonthlyExpenses] = useState(50000);
  const [inflationRate, setInflationRate] = useState(7); // Set to 7% to match your image
  const [expectedReturn, setExpectedReturn] = useState(12); // Pre-retirement return
  const [postRetirementReturn, setPostRetirementReturn] = useState(7); // Set to 7% to match your image
  
  // --- Calculation Logic ---
  const results = useMemo(() => {
    // 1. Time calculations
    const yearsToRetirement = retirementAge - presentAge;
    const yearsInRetirement = 25; // Assuming a 25-year post-retirement period

    // 2. Future Monthly Expenses (at time of retirement)
    const futureMonthlyExpenses = monthlyExpenses * (1 + inflationRate / 100) ** yearsToRetirement;
    
    // 3. Retirement Corpus (Kitty) Needed
    const r = postRetirementReturn / 100;
    const g = inflationRate / 100;
    const n = yearsInRetirement;
    const C = futureMonthlyExpenses * 12; // First year's withdrawal
    
    // --- THIS IS THE FIX ---
    let corpusNeeded;
    if (r === g) {
      // Handle divide by zero edge case (when returns = inflation)
      // Simplified formula for PV of growing annuity when r=g
      corpusNeeded = (C * n) / (1 + r);
    } else {
      // This is the standard financial formula
      corpusNeeded = (C / (r - g)) * (1 - ((1 + g) / (1 + r)) ** n);
    }
    // --- END OF FIX ---

    // 4. Monthly SIP Required to reach the corpus
    const i = expectedReturn / 100 / 12; // Monthly return rate
    const N = yearsToRetirement * 12; // Number of months to invest
    
    let monthlySip = 0;
    let totalInvested = 0;
    let totalGains = 0;

    if (N > 0 && i > 0) {
      // Future Value of SIP formula, solved for Monthly Payment (SIP)
      monthlySip = (corpusNeeded * i) / (((1 + i) ** N - 1) * (1 + i));
      totalInvested = monthlySip * N;
      totalGains = corpusNeeded - totalInvested;
    } else if (N > 0) { // Handle 0% return
      monthlySip = corpusNeeded / N;
      totalInvested = corpusNeeded;
      totalGains = 0;
    }

    return {
      corpusNeeded: Math.round(corpusNeeded),
      monthlySip: Math.round(monthlySip),
      totalInvested: Math.round(totalInvested),
      totalGains: Math.round(totalGains),
      futureMonthlyExpenses: Math.round(futureMonthlyExpenses),
      yearsToRetirement,
    };
  }, [presentAge, retirementAge, monthlyExpenses, inflationRate, expectedReturn, postRetirementReturn]);

  return (
    <div className="bg-gray-50 p-6 rounded-lg border">
      <div className="flex items-center mb-6">
        <FaUserClock className="h-8 w-8 text-blue-600 mr-3" />
        <h3 className="text-2xl font-bold text-gray-900">Plan Your Retirement</h3>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* --- Column 1: Inputs --- */}
        <div className="space-y-6">
          {/* Present Age */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Present Age</label>
              <span className="text-xl font-bold text-blue-600">{presentAge} Years</span>
            </div>
            <input
              type="range" min="18" max="60" step="1"
              value={presentAge}
              onChange={(e) => setPresentAge(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Retirement Age */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Retirement Age</label>
              <span className="text-xl font-bold text-blue-600">{retirementAge} Years</span>
            </div>
            <input
              type="range" min="40" max="70" step="1"
              value={retirementAge}
              onChange={(e) => setRetirementAge(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Monthly Expenses */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Current Monthly Expenses</label>
              <span className="text-xl font-bold text-blue-600">{formatCurrency(monthlyExpenses)}</span>
            </div>
            <input
              type="range" min="10000" max="200000" step="1000"
              value={monthlyExpenses}
              onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Assumed Inflation Rate */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Assumed Inflation Rate</label>
              <span className="text-xl font-bold text-blue-600">{inflationRate}%</span>
            </div>
            <input
              type="range" min="4" max="10" step="0.5"
              value={inflationRate}
              onChange={(e) => setInflationRate(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Expected Return Rate (Pre-Retirement) */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Expected Returns (Pre-Retirement)</label>
              <span className="text-xl font-bold text-blue-600">{expectedReturn}%</span>
            </div>
            <input
              type="range" min="5" max="20" step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Expected Return Rate (Post-Retirement) */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Expected Returns (Post-Retirement)</label>
              <span className="text-xl font-bold text-blue-600">{postRetirementReturn}%</span>
            </div>
            <input
              type="range" min="3" max="10" step="0.5"
              value={postRetirementReturn}
              onChange={(e) => setPostRetirementReturn(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* --- Column 2: Results --- */}
        <div className="bg-white rounded-xl p-6 border-2 border-blue-600 shadow-xl">
          <h3 className="text-xl font-bold text-blue-700 text-center">Your Retirement Plan</h3>
          
          <div className="text-center my-6">
            <p className="text-lg text-gray-600">You will need a corpus of</p>
            <p className="text-5xl font-extrabold text-gray-900 my-2">
              {formatCurrency(results.corpusNeeded)}
            </p>
            <p className="text-base text-gray-500">
              to cover your expenses for 25 years after retirement.
            </p>
          </div>
          
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-lg font-semibold text-gray-800 text-center">Required Investment</h4>
            
            <p className="text-3xl font-bold text-blue-600 text-center my-3">
              {formatCurrency(results.monthlySip)}
            </p>
            <p className="text-base text-gray-600 text-center">
              per month for the next {results.yearsToRetirement} years.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mt-6 space-y-3">
            <div className="flex justify-between text-base">
              <span className="text-gray-600">Total Amount Invested</span>
              <span className="font-bold text-gray-800">{formatCurrency(results.totalInvested)}</span>
            </div>
            <div className="flex justify-between text-base">
              <span className="text-gray-600">Estimated Gains</span>
              <span className="font-bold text-green-600">{formatCurrency(results.totalGains)}</span>
            </div>
            <hr/>
            <div className="flex justify-between text-base">
              <span className="text-gray-600">Future Monthly Expenses</span>
              <span className="font-bold text-gray-800">{formatCurrency(results.futureMonthlyExpenses)}</span>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}

export default RetirementCalculator;